/**
 * WebPlacementsPage (Web Funnels)
 *
 * Standalone page for managing single web funnels (shareable funnel URLs).
 * A/B tests are managed on a separate page (/web-ab-tests).
 *
 * Features:
 * - List all single web placements with status filter
 * - Create/Edit single web placements
 * - Deploy to GCS with one click
 * - Copy URL to clipboard
 *
 * @module webplacements/pages
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Chip,
  Typography,
  CircularProgress,
  Alert,
  AlertTitle,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  Paper,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Stack,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as DeployIcon,
  Language as WebIcon,
  ContentCopy as CopyIcon,
  FileCopy as FullCopyIcon,
  Check as CheckIcon,
  Search as SearchIcon,
  Home as HomeIcon,
  Cloud as CloudIcon,
  WarningAmberOutlined as SchemaUnsupportedIcon,
  CheckCircleOutlineRounded as SchemaV2Icon,
  HelpOutline as SchemaUnknownIcon,
} from '@mui/icons-material';
import { useAuth } from '@frontend/contexts/AuthContext';
import { WebPlacementDeployError } from '@modules/webplacements/types/webplacements.types';
import { useWebPlacements } from '../hooks/useWebPlacements';
import { useWebPlacementDeploy } from '../hooks/useWebPlacementDeploy';
import { useFunnelSchemaVersions, type SchemaState } from '../hooks/useFunnelSchemaVersions';
import { WebPlacementDialog } from '../components/WebPlacementDialog';
import { DeployWebPlacementDialog } from '../components/DeployWebPlacementDialog';
import { WebPlacementStatusBadge } from '../components/WebPlacementStatusBadge';
import { BulkPublishDialog } from '../components/BulkPublishDialog';
import type { WebPlacement, WebPlacementStatus } from '../types/webplacements.types';
import { getFunnelUrl, getLocalDevFunnelUrl } from '../types/webplacements.types';
import { useCustomDomain } from '@modules/custom-domains';

/**
 * Status filter type
 */
type StatusFilter = 'all' | WebPlacementStatus;

/**
 * Source-kind filter: placements created from onboarding funnels vs articles.
 */
type SourceKindFilter = 'all' | 'onboarding' | 'article';

/**
 * Sort order type
 */
type SortOrder = 'asc' | 'desc';

/**
 * Schema version indicator — renders Web Placement support status:
 *  - 'v2'          → success icon (publishable schema)
 *  - 'unsupported' → warning icon (must be migrated before publish)
 *  - 'loading'     → skeleton placeholder while detector resolves
 *  - 'error'       → neutral icon when the underlying onboarding can't be read
 *
 * `data-testid` is emitted in every state so E2E selectors are stable.
 * `aria-label` carries the same semantic ("Schema unsupported"/"Schema V2"/...)
 * that the schema filter and tests rely on.
 */
const SchemaVersionIndicator: React.FC<{ wpId: string; state: SchemaState }> = ({ wpId, state }) => {
  const testId = `webpl-schema-version-${wpId}`;

  if (state === 'loading') {
    return (
      <Tooltip title="Loading schema version…">
        <Box
          component="span"
          data-testid={testId}
          aria-label="Loading schema version"
          sx={{ display: 'inline-flex', alignItems: 'center', mt: 0.25 }}
        >
          <Skeleton variant="circular" width={16} height={16} />
        </Box>
      </Tooltip>
    );
  }

  if (state === 'unsupported') {
    return (
      <Tooltip title="Unsupported schema — migrate to V2 before publishing">
        <Box
          component="span"
          data-testid={testId}
          aria-label="Schema unsupported"
          sx={{ display: 'inline-flex', alignItems: 'center', mt: 0.25 }}
        >
          <SchemaUnsupportedIcon fontSize="small" color="warning" sx={{ fontSize: 16 }} />
        </Box>
      </Tooltip>
    );
  }

  if (state === 'v2') {
    return (
      <Tooltip title="Schema V2">
        <Box
          component="span"
          data-testid={testId}
          aria-label="Schema V2"
          sx={{ display: 'inline-flex', alignItems: 'center', mt: 0.25 }}
        >
          <SchemaV2Icon fontSize="small" color="success" sx={{ fontSize: 16 }} />
        </Box>
      </Tooltip>
    );
  }

  // state === 'error'
  return (
    <Tooltip title="Could not detect schema version">
      <Box
        component="span"
        data-testid={testId}
        aria-label="Schema unknown"
        sx={{ display: 'inline-flex', alignItems: 'center', mt: 0.25 }}
      >
        <SchemaUnknownIcon fontSize="small" sx={{ fontSize: 16, color: 'action.disabled' }} />
      </Box>
    </Tooltip>
  );
};

/**
 * Format date for display
 */
const formatDate = (isoDate?: string): string => {
  if (!isoDate) return '—';
  try {
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return isoDate;
  }
};

/**
 * WebPlacementsPage Component
 */
export function WebPlacementsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const { currentUser, isAdmin } = useAuth();

  // Load custom domain for project (to display custom domain URL if active)
  const {
    domain: customDomain,
    loading: customDomainLoading,
    hasSessionCache: hasCustomDomainSessionCache,
  } = useCustomDomain(projectId || '');
  const activeCustomDomain = customDomain?.status === 'active' ? customDomain.domain : null;
  const showCustomDomainLoading = customDomainLoading && !hasCustomDomainSessionCache;

  // Data hooks
  const {
    webPlacements,
    isLoading,
    error,
    create,
    update,
    remove,
    cloneFull,
    isAliasUnique,
    clearError,
  } = useWebPlacements(projectId || '');

  const { deploy, isDeploying, error: deployError } = useWebPlacementDeploy();

  // The deploy dialog owns the UX for the analytics privacy 422 (guided
  // variable review); suppress only that code at page level.
  const isPrivacyReviewDeployError =
    deployError instanceof WebPlacementDeployError &&
    deployError.errorCode === 'analytics_variable_privacy_review_required';

  // UI State
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sourceKindFilter, setSourceKindFilter] = useState<SourceKindFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc'); // Default: newest first

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingWebPlacement, setEditingWebPlacement] = useState<WebPlacement | null>(null);

  // Deploy dialog state
  const [deployDialogOpen, setDeployDialogOpen] = useState(false);
  const [deployingWebPlacement, setDeployingWebPlacement] = useState<WebPlacement | null>(null);

  // Bulk publish dialog state
  const [bulkPublishDialogOpen, setBulkPublishDialogOpen] = useState(false);

  // Actions menu state
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuWebPlacement, setMenuWebPlacement] = useState<WebPlacement | null>(null);

  // Delete confirmation state
  const [webPlacementToDelete, setWebPlacementToDelete] = useState<WebPlacement | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Template for duplicate flow (create dialog pre-filled with source settings)
  const [templateWebPlacement, setTemplateWebPlacement] = useState<WebPlacement | null>(null);

  // Copy state
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // All single placements (for bulk publish dialog - no filters applied)
  const singlePlacements = useMemo(() => {
    return webPlacements.filter((wp) => {
      const wpType = wp.type || 'single';
      return wpType === 'single';
    });
  }, [webPlacements]);

  // Lazy-fetch schema support for each visible placement.
  // Dedupes by (funnelId, versionId) — see useFunnelSchemaVersions doc.
  const { byPlacement: schemaByPlacement } = useFunnelSchemaVersions(
    projectId || '',
    singlePlacements
  );

  // Filter placements - only show 'single' type (A/B tests are on separate page)
  const filteredPlacements = useMemo(() => {
    // Start with single placements
    let result = singlePlacements;

    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter((wp) => wp.status === statusFilter);
    }

    // Source-kind filter (legacy docs carry no sourceKind → onboarding)
    if (sourceKindFilter === 'article') {
      result = result.filter((wp) => wp.sourceKind === 'article');
    } else if (sourceKindFilter === 'onboarding') {
      result = result.filter((wp) => wp.sourceKind !== 'article');
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (wp) =>
          wp.name.toLowerCase().includes(query) ||
          wp.alias.toLowerCase().includes(query) ||
          wp.config?.entityName?.toLowerCase().includes(query) ||
          wp.config?.funnelName?.toLowerCase().includes(query)
      );
    }

    // Sort by updatedAt
    result = [...result].sort((a, b) => {
      const dateA = new Date(a.updatedAt || 0).getTime();
      const dateB = new Date(b.updatedAt || 0).getTime();
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

    return result;
  }, [singlePlacements, statusFilter, sourceKindFilter, searchQuery, sortOrder]);

  // Handlers

  const handleSourceKindFilterChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, newFilter: SourceKindFilter | null) => {
      if (newFilter) {
        setSourceKindFilter(newFilter);
      }
    },
    []
  );

  const handleStatusFilterChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, newFilter: StatusFilter | null) => {
      if (newFilter) {
        setStatusFilter(newFilter);
      }
    },
    []
  );

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  }, []);

  const handleSortToggle = useCallback(() => {
    setSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));
  }, []);

  // Dialog handlers
  const handleCreateDialogOpen = useCallback(() => {
    setCreateDialogOpen(true);
  }, []);

  const handleCreateDialogClose = useCallback(() => {
    setCreateDialogOpen(false);
    setTemplateWebPlacement(null);
  }, []);

  const handleEditDialogClose = useCallback(() => {
    setEditDialogOpen(false);
    setEditingWebPlacement(null);
  }, []);

  const handleActionsMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>, wp: WebPlacement) => {
      event.stopPropagation();
      setActionsMenuAnchor(event.currentTarget);
      setMenuWebPlacement(wp);
    },
    []
  );

  const handleActionsMenuClose = useCallback(() => {
    setActionsMenuAnchor(null);
    setMenuWebPlacement(null);
  }, []);

  const handleEditClick = useCallback(() => {
    if (menuWebPlacement) {
      setEditingWebPlacement(menuWebPlacement);
      setEditDialogOpen(true);
    }
    handleActionsMenuClose();
  }, [menuWebPlacement, handleActionsMenuClose]);

  const handleDeployClick = useCallback(() => {
    if (menuWebPlacement) {
      setDeployingWebPlacement(menuWebPlacement);
      setDeployDialogOpen(true);
    }
    handleActionsMenuClose();
  }, [menuWebPlacement, handleActionsMenuClose]);

  const handleDeleteClick = useCallback(() => {
    if (menuWebPlacement) {
      setWebPlacementToDelete(menuWebPlacement);
      setDeleteDialogOpen(true);
    }
    handleActionsMenuClose();
  }, [menuWebPlacement, handleActionsMenuClose]);

  const handleDeleteCancel = useCallback(() => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setWebPlacementToDelete(null);
  }, [deleteLoading]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!webPlacementToDelete) return;

    setDeleteLoading(true);
    try {
      await remove(webPlacementToDelete.id);
      setDeleteDialogOpen(false);
      setWebPlacementToDelete(null);
    } catch (err) {
      console.error('Failed to delete:', err);
    } finally {
      setDeleteLoading(false);
    }
  }, [webPlacementToDelete, remove]);

  const handleDuplicateClick = useCallback(() => {
    if (!menuWebPlacement) return;
    handleActionsMenuClose();
    setTemplateWebPlacement(menuWebPlacement);
    setCreateDialogOpen(true);
  }, [menuWebPlacement, handleActionsMenuClose]);

  const handleFullCopyClick = useCallback(async () => {
    if (!menuWebPlacement || !currentUser?.email) return;

    try {
      const cloned = await cloneFull(menuWebPlacement.id, currentUser.email);
      setEditingWebPlacement(cloned);
      setEditDialogOpen(true);
    } catch (err) {
      console.error('Failed to full-copy web placement:', err);
    } finally {
      handleActionsMenuClose();
    }
  }, [menuWebPlacement, currentUser?.email, cloneFull, handleActionsMenuClose]);

  const handleRowClick = useCallback((wp: WebPlacement) => {
    setEditingWebPlacement(wp);
    setEditDialogOpen(true);
  }, []);

  const handleCopyUrl = useCallback(
    async (event: React.MouseEvent, wp: WebPlacement) => {
      event.stopPropagation();
      const url = getFunnelUrl(wp.alias, activeCustomDomain);
      try {
        await navigator.clipboard.writeText(url);
        setCopiedId(wp.id);
        setTimeout(() => setCopiedId(null), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    },
    [activeCustomDomain]
  );

  const handleOpenUrl = useCallback(
    (event: React.MouseEvent, wp: WebPlacement) => {
      event.stopPropagation();
      const url = getFunnelUrl(wp.alias, activeCustomDomain);
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    [activeCustomDomain]
  );

  const handleDeployDialogClose = useCallback(() => {
    setDeployDialogOpen(false);
    setDeployingWebPlacement(null);
  }, []);

  const handleDeployAfterSave = useCallback((wp: WebPlacement) => {
    setCreateDialogOpen(false);
    setEditDialogOpen(false);
    setEditingWebPlacement(null);
    setTemplateWebPlacement(null);
    setDeployingWebPlacement(wp);
    setDeployDialogOpen(true);
  }, []);

  // Get display URL based on custom domain and admin status
  const getDisplayUrl = useCallback(
    (alias: string): { url: string; isDevUrl: boolean } => {
      if (activeCustomDomain) {
        return { url: getFunnelUrl(alias, activeCustomDomain), isDevUrl: false };
      }
      // Admins see dev URL for testing
      if (isAdmin) {
        return { url: getFunnelUrl(alias, null), isDevUrl: true };
      }
      // Non-admins without domain also see dev URL but it won't work
      return { url: getFunnelUrl(alias, null), isDevUrl: true };
    },
    [activeCustomDomain, isAdmin]
  );

  const handleDeploy = useCallback(
    async (webPlacementId: string) => {
      // Block non-admins without custom domain
      if (!activeCustomDomain && !isAdmin) {
        return {
          success: false,
          webUrl: '',
          gcsFiles: [],
          error: 'Custom domain is required to publish. Please configure a domain first.',
        };
      }

      if (!projectId || !currentUser?.email) {
        return {
          success: false,
          webUrl: '',
          gcsFiles: [],
          error: 'Missing project or user',
        };
      }

      return deploy({
        projectId,
        webPlacementId,
        userId: currentUser.email,
      });
    },
    [projectId, currentUser, deploy, activeCustomDomain, isAdmin]
  );

  const handleCreate = useCallback(
    async (data: Parameters<typeof create>[0]) => {
      if (!currentUser?.email) throw new Error('User not authenticated');
      return create(data, currentUser.email);
    },
    [create, currentUser]
  );

  // Guard
  if (!projectId || !currentUser) {
    return null;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" component="h1">
            Web Funnels
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create shareable URLs for your onboarding funnels
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<DeployIcon />}
            onClick={() => setBulkPublishDialogOpen(true)}
            disabled={showCustomDomainLoading || (!activeCustomDomain && !isAdmin) || singlePlacements.length === 0}
            data-testid="webpl-publish-all-btn"
          >
            Publish All
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateDialogOpen}
            disabled={showCustomDomainLoading || (!activeCustomDomain && !isAdmin)}
            data-testid="webpl-create-btn"
          >
            Create Web Funnel
          </Button>
        </Box>
      </Box>

      {showCustomDomainLoading && (
        <Paper
          data-testid="webpl-domain-loading-dialog"
          sx={{
            mb: 3,
            minHeight: 280,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
        >
          <Box
            role="status"
            aria-live="polite"
            data-testid="webpl-domain-loading-status"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.25,
              px: 3,
              py: 3,
            }}
          >
            <CircularProgress />
            <Typography id="webpl-domain-loading-title" variant="subtitle1" sx={{ fontWeight: 600 }}>
              Checking custom domain...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360 }}>
              Preparing Web Funnel links
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Custom Domain Alert */}
      {!activeCustomDomain && !isLoading && !showCustomDomainLoading && !isAdmin && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Custom Domain Required</AlertTitle>
          Web Funnels require a custom domain to function.
          Please configure a custom domain before creating funnels.
        </Alert>
      )}
      {!activeCustomDomain && !isLoading && !showCustomDomainLoading && isAdmin && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>No Custom Domain (Admin Mode)</AlertTitle>
          You can create funnels for testing, but they will only work after a custom domain is configured.
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Status filter */}
        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          onChange={handleStatusFilterChange}
          size="small"
          data-testid="webpl-status-filter"
        >
          <ToggleButton value="all" data-testid="webpl-status-all">All Status</ToggleButton>
          <ToggleButton value="draft" data-testid="webpl-status-draft">Draft</ToggleButton>
          <ToggleButton value="published" data-testid="webpl-status-published">Published</ToggleButton>
        </ToggleButtonGroup>

        {/* Source-kind filter: onboarding funnels vs article placements */}
        <ToggleButtonGroup
          value={sourceKindFilter}
          exclusive
          onChange={handleSourceKindFilterChange}
          size="small"
          data-testid="webpl-source-filter"
        >
          <ToggleButton value="all" data-testid="webpl-source-all">All</ToggleButton>
          <ToggleButton value="onboarding" data-testid="webpl-source-onboardings">Onboardings</ToggleButton>
          <ToggleButton value="article" data-testid="webpl-source-articles">Articles</ToggleButton>
        </ToggleButtonGroup>

        <TextField
          size="small"
          placeholder="Search..."
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{ width: 300 }}
          data-testid="webpl-search-input"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>

      {/* Loading state */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error state. The analytics privacy 422 is owned by the deploy
          dialog's guided review — never render its raw sentence here. */}
      {(error || (deployError && !isPrivacyReviewDeployError)) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {error?.message || deployError?.message}
        </Alert>
      )}

      {/* Empty state */}
      {!isLoading && !showCustomDomainLoading && !error && filteredPlacements.length === 0 && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Stack spacing={3} alignItems="center">
            <WebIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
            <Typography variant="h6" color="text.secondary">
              {searchQuery ? 'No funnels match your filters' : 'No web funnels yet'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchQuery
                ? 'Try adjusting your search or filter criteria'
                : 'Create your first web funnel to share onboarding flows via URL'}
            </Typography>
            {!searchQuery && (
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<AddIcon />}
                onClick={handleCreateDialogOpen}
              >
                Create Web Funnel
              </Button>
            )}
          </Stack>
        </Paper>
      )}

      {/* Table */}
      {!isLoading && !showCustomDomainLoading && !error && filteredPlacements.length > 0 && (
        <Paper>
          <Table data-testid="webpl-table">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell width={200}>URL</TableCell>
                <TableCell width={200}>Onboarding</TableCell>
                <TableCell width={100}>Status</TableCell>
                <TableCell width={150} sortDirection={sortOrder}>
                  <TableSortLabel
                    active
                    direction={sortOrder}
                    onClick={handleSortToggle}
                  >
                    Updated
                  </TableSortLabel>
                </TableCell>
                <TableCell width={60} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPlacements.map((wp) => (
                <TableRow
                  key={wp.id}
                  hover
                  onClick={() => handleRowClick(wp)}
                  sx={{ cursor: 'pointer' }}
                  data-testid={`webpl-row-${wp.id}`}
                >
                  {/* Name */}
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {wp.name}
                    </Typography>
                    {wp.description && (
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {wp.description}
                      </Typography>
                    )}
                  </TableCell>

                  {/* URL */}
                  <TableCell>
                    {(() => {
                      const { url, isDevUrl } = getDisplayUrl(wp.alias);
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <WebIcon
                            fontSize="small"
                            sx={{ color: isDevUrl ? 'warning.main' : 'secondary.main', fontSize: 16 }}
                          />
                          <Tooltip
                            title={isDevUrl ? '⚠️ Dev URL - may not work without custom domain' : ''}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.8125rem',
                                color: isDevUrl ? 'warning.main' : 'secondary.main',
                              }}
                            >
                              {url}
                            </Typography>
                          </Tooltip>
                          <Tooltip title={copiedId === wp.id ? 'Copied!' : 'Copy URL'}>
                            <IconButton
                              size="small"
                              onClick={(e) => handleCopyUrl(e, wp)}
                              sx={{ p: 0.25 }}
                              data-testid={`webpl-copy-url-${wp.id}`}
                            >
                              {copiedId === wp.id ? (
                                <CheckIcon sx={{ fontSize: 14, color: 'success.main' }} />
                              ) : (
                                <CopyIcon sx={{ fontSize: 14 }} />
                              )}
                            </IconButton>
                          </Tooltip>
                          {/* Local dev link with debug panel */}
                          <Tooltip title="Open Local - with Debug Panel">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(getLocalDevFunnelUrl(wp.alias), '_blank');
                              }}
                              sx={{ p: 0.25 }}
                              data-testid={`webpl-open-local-${wp.id}`}
                            >
                              <HomeIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                            </IconButton>
                          </Tooltip>
                          {/* Cloud link */}
                          {wp.status === 'published' && (
                            <Tooltip
                              title={
                                activeCustomDomain
                                  ? `Open (${activeCustomDomain})`
                                  : 'Open Cloud (dev-api.segmently.ai)'
                              }
                            >
                              <IconButton
                                size="small"
                                onClick={(e) => handleOpenUrl(e, wp)}
                                sx={{ p: 0.25 }}
                                data-testid={`webpl-open-cloud-${wp.id}`}
                              >
                                <CloudIcon
                                  sx={{
                                    fontSize: 14,
                                    color: isDevUrl ? 'warning.main' : 'success.main',
                                  }}
                                />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      );
                    })()}
                  </TableCell>

                  {/* Onboarding */}
                  <TableCell>
                    <Typography variant="body2" color="text.primary" noWrap>
                      {wp.config?.funnelName || wp.config?.funnelId || '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {wp.config?.versionName || wp.config?.versionId || '—'}
                    </Typography>
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <WebPlacementStatusBadge status={wp.status} />
                      {wp.sourceKind === 'article' && (
                        <Chip
                          label="Article"
                          color="info"
                          variant="outlined"
                          size="small"
                          data-testid={`placement-article-badge-${wp.id}`}
                        />
                      )}
                      <SchemaVersionIndicator
                        wpId={wp.id}
                        state={schemaByPlacement.get(wp.id) ?? 'loading'}
                      />
                    </Box>
                    {wp.webShellVersion && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        v{wp.webShellVersion}
                      </Typography>
                    )}
                  </TableCell>

                  {/* Updated date */}
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(wp.updatedAt)}
                    </Typography>
                  </TableCell>

                  {/* Actions */}
                  <TableCell align="right">
                    <IconButton size="small" onClick={(e) => handleActionsMenuOpen(e, wp)} data-testid={`webpl-actions-${wp.id}`}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={actionsMenuAnchor}
        open={Boolean(actionsMenuAnchor)}
        onClose={handleActionsMenuClose}
      >
        <MenuItem onClick={handleDeployClick} data-testid="webpl-menu-publish">
          <ListItemIcon>
            <DeployIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Publish</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleEditClick} data-testid="webpl-menu-edit">
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDuplicateClick} data-testid="webpl-menu-duplicate">
          <ListItemIcon>
            <CopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleFullCopyClick} data-testid="webpl-menu-full-copy">
          <ListItemIcon>
            <FullCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Full Copy</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }} data-testid="webpl-menu-delete">
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog
        open={deleteDialogOpen}
        onClose={deleteLoading ? undefined : handleDeleteCancel}
        aria-labelledby="webpl-delete-confirm-title"
        data-testid="webpl-delete-confirm-dialog"
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.15)',
              backdropFilter: 'blur(20px)',
            },
          },
        }}
      >
        <DialogTitle
          id="webpl-delete-confirm-title"
          sx={{
            display: 'grid',
            gridTemplateColumns: '40px 1fr 40px',
            alignItems: 'center',
            gap: 1,
            pb: 1,
          }}
        >
          <Box />
          <Box
            component="span"
            sx={{
              textAlign: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            Delete Web Funnel?
          </Box>
          <Box />
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography>
            Delete "{webPlacementToDelete?.name}" ({webPlacementToDelete?.alias})?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleDeleteCancel}
            disabled={deleteLoading}
            data-testid="webpl-delete-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            data-testid="webpl-delete-confirm-btn"
          >
            {deleteLoading ? <CircularProgress size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Dialog (also used for duplicate with templateWebPlacement) */}
      <WebPlacementDialog
        open={createDialogOpen}
        onClose={handleCreateDialogClose}
        onCreate={handleCreate}
        onDeployRequested={handleDeployAfterSave}
        projectId={projectId}
        userId={currentUser.email || ''}
        mode="create"
        isAliasUnique={isAliasUnique}
        templateWebPlacement={templateWebPlacement || undefined}
      />

      {/* Edit Dialog */}
      {editingWebPlacement && (
        <WebPlacementDialog
          open={editDialogOpen}
          onClose={handleEditDialogClose}
          onUpdate={update}
          onDeployRequested={handleDeployAfterSave}
          projectId={projectId}
          userId={currentUser.email || ''}
          mode="edit"
          webPlacement={editingWebPlacement}
          isAliasUnique={isAliasUnique}
        />
      )}

      {/* Deploy Dialog */}
      <DeployWebPlacementDialog
        open={deployDialogOpen}
        onClose={handleDeployDialogClose}
        webPlacement={deployingWebPlacement}
        onDeploy={handleDeploy}
        isDeploying={isDeploying}
        projectId={projectId}
      />

      {/* Bulk Publish Dialog */}
      <BulkPublishDialog
        open={bulkPublishDialogOpen}
        onClose={() => setBulkPublishDialogOpen(false)}
        webPlacements={singlePlacements}
        onPublish={handleDeploy}
        mode="publish_all"
        onReviewPlacement={(webPlacementId) => {
          // Privacy-gated item: hand off to the single deploy dialog, which
          // owns the in-dialog analytics variable review flow.
          const placement = singlePlacements.find((wp) => wp.id === webPlacementId);
          if (!placement) return;
          setBulkPublishDialogOpen(false);
          setDeployingWebPlacement(placement);
          setDeployDialogOpen(true);
        }}
      />
    </Box>
  );
}

export default WebPlacementsPage;
